import type { InboundMessage, OutboundMessage } from './protocol';

/**
 * Transport layer for the host ↔ forms bridge.
 *
 * Detection is done once per page load. In both targets the receiving side
 * uses `window.addEventListener('message', ...)` so subscription is identical.
 * Only emission differs:
 *
 *   - iframe target → `window.parent.postMessage(...)`
 *   - React Native WebView → `window.ReactNativeWebView.postMessage(...)`
 *
 * # Origin pinning (web only)
 *
 * Which origins may embed the forms-host is controlled by infra via the
 * `frame-ancestors` CSP header, so we don't keep our own embed allowlist.
 * We still pin the outbound `targetOrigin` to avoid broadcasting our
 * messages with `'*'`:
 *
 * 1. The first inbound `init` pins `targetOrigin` to its `event.origin`
 *    for all subsequent outbound messages.
 * 2. In dev (`import.meta.env.DEV`) `targetOrigin` falls back to `'*'`, so
 *    the mockInit fixture and LAN-exposed mobile testing continue to work.
 */

declare global {
  interface Window {
    ReactNativeWebView?: { postMessage: (data: string) => void };
  }
}

const isReactNativeWebView = (): boolean =>
  typeof window !== 'undefined' && typeof window.ReactNativeWebView !== 'undefined';

const isInIframe = (): boolean =>
  typeof window !== 'undefined' && window.parent !== window;

const isDev = import.meta.env.DEV;

/**
 * Pinned target origin for outbound messages. Set on the first inbound
 * `init`. `null` until then — outbound calls drop on the floor rather than
 * leak to `'*'` in production.
 */
let pinnedTargetOrigin: string | null = null;

/** Test-only: reset the pinned origin. */
export const __resetPinnedOriginForTests = (): void => {
  pinnedTargetOrigin = null;
};

/** Returns the origin to use for `postMessage`. */
const resolveTargetOrigin = (): string | null => {
  if (isDev) return '*';
  return pinnedTargetOrigin;
};

/** A real, pinnable origin (excludes sandboxed/file `'null'` origins). */
const isPinnableOrigin = (origin: string): boolean => !!origin && origin !== 'null';

/** Posts a message to the host. No-op if there is no detected host. */
export const postOutbound = (msg: OutboundMessage): void => {
  if (typeof window === 'undefined') return;
  const payload = JSON.stringify(msg);

  if (isReactNativeWebView()) {
    // React Native ignores `targetOrigin`; the WebView is the only listener.
    window.ReactNativeWebView!.postMessage(payload);
    return;
  }

  if (isInIframe()) {
    const target = resolveTargetOrigin();
    if (target === null) {
      // No host origin pinned yet (and not dev). The only outbound message
      // that can fire before `init` is `ready`, which carries no secret
      // payload — just our `BRIDGE_VERSION`. Allowing it through with '*'
      // keeps the documented handshake working without leaking anything
      // sensitive. Anything else gets dropped until the pin lands.
      if (msg.type === 'ready') {
        window.parent.postMessage(payload, '*');
      }
      return;
    }
    window.parent.postMessage(payload, target);
  }
};

/**
 * Subscribes to messages from the host. Returns an unsubscribe function.
 * Accepts either a stringified JSON payload or an already-parsed object.
 *
 * Embedding is restricted by infra via `frame-ancestors`, so we don't gate
 * inbound origins here. The first `init` pins the outbound `targetOrigin`.
 */
export const subscribeInbound = (handler: (msg: InboundMessage) => void): (() => void) => {
  const listener = (event: MessageEvent) => {
    // React Native WebView delivers messages with `event.origin === ''`.
    const isRn = isReactNativeWebView();

    let parsed: unknown;
    if (typeof event.data === 'string') {
      try {
        parsed = JSON.parse(event.data);
      } catch {
        return;
      }
    } else {
      parsed = event.data;
    }

    if (
      parsed &&
      typeof parsed === 'object' &&
      'type' in parsed &&
      typeof (parsed as { type: unknown }).type === 'string'
    ) {
      const msg = parsed as InboundMessage;

      // Pin the outbound origin to whichever origin sent the first `init`.
      // Only effective in production; dev keeps '*'.
      if (
        !isDev &&
        !isRn &&
        msg.type === 'init' &&
        pinnedTargetOrigin === null &&
        isPinnableOrigin(event.origin)
      ) {
        pinnedTargetOrigin = event.origin;
      }

      handler(msg);
    }
  };

  window.addEventListener('message', listener);
  return () => window.removeEventListener('message', listener);
};
